import { type ComputedRef, nextTick, type Ref, ref, watch } from 'vue';

import { isComponentWithEl, toElement } from '../../utils/templateRefNarrowing';

interface UseMoreDropdownWidthOptions {
  moreDropdownRef: Ref<unknown>;
  actionsContainerRef: Ref<unknown>;
  moreButtonAlign: 'separate' | 'together';
  hasOverflowActions: ComputedRef<boolean>;
}

export function useMoreButtonWidth({
  moreDropdownRef,
  actionsContainerRef,
  moreButtonAlign,
  hasOverflowActions,
}: UseMoreDropdownWidthOptions) {
  /**
   * Calculates the width of the More dropdown with gap consideration
   * When moreButtonAlign is 'separate', adds the gap between actions container and dropdown
   * When moreButtonAlign is 'together', returns only the dropdown width
   */
  const calculateMoreButtonWidth = (): number => {
    const dropdown = moreDropdownRef.value;
    if (!isComponentWithEl(dropdown)) return 0;

    const dropdownWidth = dropdown.$el.getBoundingClientRect().width;

    // If moreButtonAlign is separate, add gap between actions container and dropdown
    if (moreButtonAlign === 'separate') {
      const container = toElement(actionsContainerRef.value);
      if (!container) return dropdownWidth;

      const computedStyle = window.getComputedStyle(container);
      const gapWidth = parseFloat(computedStyle.gap) || 0;
      return dropdownWidth + gapWidth;
    }

    return dropdownWidth;
  };

  const moreButtonWidth = ref(0);

  watch(hasOverflowActions, (newValue) => {
    if (newValue && moreDropdownRef.value) {
      nextTick(() => {
        const calculatedWidth = calculateMoreButtonWidth();
        if (calculatedWidth > 0) {
          moreButtonWidth.value = calculatedWidth;
        }
      });
    }
  });

  return {
    calculateMoreButtonWidth,
    moreButtonWidth,
  };
}
